import wsPrintClient from './wsPrintClient';
import notaanota from './notaanota';
import { message } from 'antd';

const WEB_BLUETOOTH_TYPE = 'webbluetooth';
const WEB_BLUETOOTH_CHUNK_SIZE = 512;
const ZEBRA_BLE_SERVICE_UUID = '38eb4a80-c570-11e3-9507-0002a5d5c51b';
const ZEBRA_BLE_WRITE_CHARACTERISTIC_UUID = '38eb4a82-c570-11e3-9507-0002a5d5c51b';
const VALID_PRINTER_TYPES = ['bluetooth', 'websocket', 'socket', WEB_BLUETOOTH_TYPE];
const INVALID_PRINTER_NAMES = new Set(['bluetooth', 'websocket', 'socket']);

const sanitizePrinterName = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const normalized = raw
    .replace(/^bluetooth[\s:|-]*/i, '')
    .replace(/^websocket[\s:|-]*/i, '')
    .replace(/^socket[\s:|-]*/i, '')
    .trim();

  if (!normalized) return null;
  if (INVALID_PRINTER_NAMES.has(normalized.toLowerCase())) return null;

  return normalized;
};

const extractPrinterName = (input) => {
  if (input == null) return null;

  if (typeof input === 'string') {
    const parsed = input.trim();
    if (!parsed) return null;

    if ((parsed.startsWith('{') && parsed.endsWith('}')) || (parsed.startsWith('[') && parsed.endsWith(']'))) {
      try {
        const nested = extractPrinterName(JSON.parse(parsed));
        if (nested) return nested;
      } catch (error) {
        void error;
      }
    }

    return sanitizePrinterName(parsed);
  }

  if (typeof input === 'object') {
    const candidates = [
      input.deviceName,
      input.printerName,
      input.nome,
      input.name,
      input.label,
      input.device,
      input.impressora,
      input.selectedPrinter,
      input.printer,
      input?.device?.name,
      input?.device?.nome,
      input?.device?.label,
      input?.payload?.deviceName,
      input?.payload?.printerName,
      input?.payload?.name,
      input?.payload?.nome,
    ];

    for (const candidate of candidates) {
      const normalized = extractPrinterName(candidate);
      if (normalized) return normalized;
    }
  }

  return null;
};
/**
 * Serviço para gerenciar conexões de impressoras
 * Suporta Bluetooth (Android) e WebSocket (Etiquetando Print)
 */

class PrinterService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.connectionType = null;
    this.printerType = null;
    this.printerName = null;
    this.deviceInfo = null;
    this.activeLabelCode = null;
    this.currentUser = null;
    this.webBluetoothDevice = null;
    this.webBluetoothServer = null;
    this.webBluetoothCharacteristic = null;
    this.handleWebBluetoothDisconnected = this.handleWebBluetoothDisconnected.bind(this);
    this.callbacks = {
      onConnect: null,
      onDisconnect: null,
      onError: null,
      onDeviceList: null
    };
  }

  syncState(state = {}) {
    const connected = typeof state.connected === 'boolean' ? state.connected : undefined;
    const name = extractPrinterName(state.name);
    const type = typeof state.type === 'string' ? state.type : undefined;
    const device = state.device && typeof state.device === 'object' ? state.device : undefined;
    if (connected !== undefined) this.isConnected = connected;
    if (name) this.printerName = name;
    if (type && VALID_PRINTER_TYPES.includes(type)) {
      this.connectionType = type === 'socket' ? 'socket' : type;
      this.printerType = type;
    }
    if (device) {
      const normalizedDeviceName = extractPrinterName(device);
      this.deviceInfo = { ...(this.deviceInfo || {}), ...device };
      if (normalizedDeviceName) this.deviceInfo.nome = normalizedDeviceName;
    }
  }

  /**
   * Registra callbacks para eventos da impressora
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  setActiveLabelCode(code) {
    const v = typeof code === 'string' ? code : null;
    this.activeLabelCode = v && v.trim() !== '' ? v : null;
  }

  async ensureActiveLabelCode() {
    if (!this.activeLabelCode || this.activeLabelCode === '') {
      const savedLayout = localStorage.getItem('layoutEtiqueta');
      if (savedLayout) {
        this.activeLabelCode = savedLayout;
      }
    }
    return this.activeLabelCode || null;
  }

  setUser(user) {
    if (user && typeof user === 'object') {
      this.currentUser = user;
    } else {
      this.currentUser = null;
    }
  }

  setprintertype(type) {
    if (VALID_PRINTER_TYPES.includes(type)) {
      this.printerType = type;
      this.connectionType = type === 'socket' ? 'socket' : this.connectionType;
      localStorage.setItem('printerType', type);
    } else {
      console.error('Tipo de impressora inválido. Use "bluetooth", "webbluetooth" ou "websocket".');
    }
  }

  setPrinterType(type) {
    return this.setprintertype(type);
  }

  /**
   * Lista dispositivos Bluetooth disponíveis
   * Funciona apenas em dispositivos Android com interface nativa
   */
  async listBluetoothDevices() {
    try {
      if (!window.Android) {
        throw new Error('Funcionalidade disponível apenas em dispositivos Android');
      }

      // Chama a função nativa do Android
      const result = await window.Android.listaBluetooth();

      let devices = [];
      try {
        if (typeof result === 'string') {
          devices = JSON.parse(result);
        } else {
          devices = result;
        }
      } catch (e) {
        console.error('Erro ao fazer parse da lista de dispositivos Bluetooth:', e);
        devices = [];
      }

      if (this.callbacks.onDeviceList) {
        this.callbacks.onDeviceList(devices);
      }

      return devices;
    } catch (error) {
      console.error('Erro ao listar dispositivos Bluetooth:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message);
      }
      throw error;
    }
  }

  /**
   * Conecta a um dispositivo Bluetooth
   */
  async connectBluetooth(deviceAddress) {
    try {
      if (!window.Android) {
        throw new Error('Funcionalidade disponível apenas em dispositivos Android');
      }

      const addr = (typeof deviceAddress === 'string')
        ? deviceAddress
        : (deviceAddress?.address || deviceAddress?.mac || deviceAddress?.id || deviceAddress?.uuid || deviceAddress?.device || deviceAddress?.nome || deviceAddress?.name || '');
      const nome = (typeof deviceAddress === 'string')
        ? null
        : extractPrinterName(deviceAddress);

      if (!addr || String(addr).trim() === '') {
        throw new Error('Endereço/ID do dispositivo Bluetooth inválido');
      }

      await window.Android.conectarBluetooth(addr);

      this.connectionType = 'bluetooth';
      this.printerType = 'bluetooth';
      this.isConnected = true;
      this.printerName = nome || extractPrinterName(deviceAddress) || sanitizePrinterName(addr) || addr;
      this.deviceInfo = { ...(this.deviceInfo || {}), address: addr, nome: this.printerName || undefined };

      // Persistir no localStorage para que ao recarregar o app saiba qual impressora estava conectada
      try {
        localStorage.setItem('printerType', 'bluetooth');
        localStorage.setItem('printerName', this.printerName);
      } catch (e) { void e; }

      if (this.callbacks.onConnect) {
        this.callbacks.onConnect({ type: 'bluetooth', device: this.printerName, status: 'connected' });
      }

      return true;
    } catch (error) {
      console.error('Erro ao conectar Bluetooth:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message);
      }
      throw error;
    }
  }

  /**
   * Conecta via WebSocket (Etiquetando Print)
   */
  async connectWebSocket(uuid) {
    return new Promise((resolve, reject) => {
      try {
        const user = this.currentUser || null;
        let clientUUID = null;
        try {
          clientUUID = localStorage.getItem('client_uuid');
          if (!clientUUID) {
            clientUUID = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
            localStorage.setItem('client_uuid', clientUUID);
          }
        } catch (error) {
          void error;
          clientUUID = String(Date.now());
        }

        const ident = {
          id: clientUUID,
          branches: { sigla: user?.assignedBranch?.sigla || user?.assignedBranch?.acronym || '' },
          server: false,
          computer: window.navigator.userAgent,
          codigo: user?.codigo || 'on board',
          name: user?.name || 'on board',
          emparelhar: true,
          versao: 'on board',
          pairWithUuid: String(uuid),
        };

        wsPrintClient.initializeWebsocket(ident)
          .then(() => {
            this.isConnected = true;
            this.connectionType = 'websocket';
            this.printerType = 'websocket';
            this.deviceInfo = { uuid };
            resolve(true);
          })
          .catch((error) => {
            if (this.callbacks.onError) {
              this.callbacks.onError('Erro na conexío WebSocket');
            }
            reject(error);
          });
      } catch (error) {
        if (this.callbacks.onError) {
          this.callbacks.onError(error.message);
        }
        reject(error);
      }
    });
  }

  isWebBluetoothSupported() {
    return typeof navigator !== 'undefined' && !!navigator.bluetooth;
  }

  isWebBluetoothSecureContext() {
    if (typeof window === 'undefined') return false;
    const hostname = window.location?.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalhost) return true;
    if (window.isSecureContext) return true;
    return window.location?.protocol === 'https:';
  }

  clearWebBluetoothState({ notifyDisconnect = false } = {}) {
    if (this.webBluetoothDevice && this.handleWebBluetoothDisconnected) {
      try {
        this.webBluetoothDevice.removeEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);
      } catch (error) {
        void error;
      }
    }

    this.webBluetoothDevice = null;
    this.webBluetoothServer = null;
    this.webBluetoothCharacteristic = null;

    if (this.connectionType === WEB_BLUETOOTH_TYPE) {
      this.isConnected = false;
      this.connectionType = null;
      this.printerType = null;
      this.printerName = null;
      this.deviceInfo = null;
    }

    if (notifyDisconnect && this.callbacks.onDisconnect) {
      this.callbacks.onDisconnect();
    }
  }

  handleWebBluetoothDisconnected(event) {
    const deviceName = event?.target?.name || this.printerName || 'impressora BLE';
    this.clearWebBluetoothState({ notifyDisconnect: true });
    try {
      localStorage.removeItem('printerType');
      localStorage.removeItem('printerName');
    } catch (error) {
      void error;
    }
    console.log(`Dispositivo ${deviceName} desconectado.`);
  }

  async connectWebBluetooth() {
    try {
      if (!this.isWebBluetoothSupported()) {
        throw new Error('Seu navegador não suporta Web Bluetooth. Use Chrome ou Edge no Windows.');
      }
      if (!this.isWebBluetoothSecureContext()) {
        throw new Error('Web Bluetooth requer HTTPS ou localhost.');
      }

      const device = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'Zebra' }, { namePrefix: 'ZQ' }],
        optionalServices: [ZEBRA_BLE_SERVICE_UUID],
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(ZEBRA_BLE_SERVICE_UUID);
      const characteristic = await service.getCharacteristic(ZEBRA_BLE_WRITE_CHARACTERISTIC_UUID);

      try {
        device.removeEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);
      } catch (error) {
        void error;
      }
      device.addEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);

      this.webBluetoothDevice = device;
      this.webBluetoothServer = server;
      this.webBluetoothCharacteristic = characteristic;
      this.connectionType = WEB_BLUETOOTH_TYPE;
      this.printerType = WEB_BLUETOOTH_TYPE;
      this.isConnected = true;
      this.printerName = device.name || 'Zebra BLE';
      this.deviceInfo = {
        id: device.id || null,
        nome: device.name || 'Zebra BLE',
        transport: 'ble',
        serviceUuid: ZEBRA_BLE_SERVICE_UUID,
        characteristicUuid: ZEBRA_BLE_WRITE_CHARACTERISTIC_UUID,
      };

      localStorage.setItem('printerType', WEB_BLUETOOTH_TYPE);
      localStorage.setItem('printerName', this.printerName);

      if (this.callbacks.onConnect) {
        this.callbacks.onConnect({
          type: WEB_BLUETOOTH_TYPE,
          device: this.printerName,
          status: 'connected',
        });
      }

      return {
        name: this.printerName,
        id: device.id || null,
        characteristic,
      };
    } catch (error) {
      console.error('Erro ao conectar via Web Bluetooth:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message || 'Erro ao conectar via Web Bluetooth');
      }
      throw error;
    }
  }

  async disconnectWebBluetooth() {
    try {
      const device = this.webBluetoothDevice;
      if (device && this.handleWebBluetoothDisconnected) {
        try {
          device.removeEventListener('gattserverdisconnected', this.handleWebBluetoothDisconnected);
        } catch (error) {
          void error;
        }
      }
      if (device?.gatt?.connected) {
        device.gatt.disconnect();
      }
      this.clearWebBluetoothState();
      return true;
    } catch (error) {
      console.error('Erro ao desconectar Web Bluetooth:', error);
      this.clearWebBluetoothState();
      throw error;
    }
  }

  async writeWebBluetoothChunks(textoRaw, chunkSize = WEB_BLUETOOTH_CHUNK_SIZE) {
    if (!this.webBluetoothCharacteristic) {
      throw new Error('Impressora Web Bluetooth não conectada');
    }

    const encoder = new TextEncoder();
    const bytes = encoder.encode(String(textoRaw || ''));
    const writer = typeof this.webBluetoothCharacteristic.writeValueWithoutResponse === 'function'
      ? this.webBluetoothCharacteristic.writeValueWithoutResponse.bind(this.webBluetoothCharacteristic)
      : this.webBluetoothCharacteristic.writeValue.bind(this.webBluetoothCharacteristic);

    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      const chunk = bytes.slice(offset, offset + chunkSize);
      await writer(chunk);
    }

    return true;
  }

  /**
   * Processa mensagens recebidas via WebSocket
   */
  handleWebSocketMessage(message) {
    try {

      let data = null;
      if (typeof message === 'string' && message.includes('{') && message.includes('}')) {
        try { data = JSON.parse(message); } catch (e) { void e; }
      }
      if (data && typeof data === 'object') {
        const keys = Object.keys(data);
        if (keys.includes('pareado') || keys.includes('parear')) {
          this.isConnected = true;
          this.deviceInfo = { ...(this.deviceInfo || {}), nome: '' };
          return;
        }
        if (keys.includes('sel_impressora') || keys.includes('impressora') || keys.includes('nome')) {
          const nome = extractPrinterName(data);
          if (nome) {
            this.isConnected = true;
            this.connectionType = 'socket';
            this.printerName = nome;
            this.deviceInfo = { ...(this.deviceInfo || {}), nome };
            if (this.callbacks.onConnect) {
              this.callbacks.onConnect({ type: this.connectionType || 'websocket', device: nome, status: 'connected' });
            }
          }
          return;
        }
        if (keys.includes('retorno_impressao_ok')) {
          if (this.callbacks.onMessage) this.callbacks.onMessage('retorno_impressao_ok');
          return;
        }
        if (keys.includes('retorno_impressao_error')) {
          if (this.callbacks.onError) this.callbacks.onError('retorno_impressao_error');
          return;
        }
      }
      if (message === 'retorno_impressao_ok') {
        if (this.callbacks.onMessage) this.callbacks.onMessage('retorno_impressao_ok');
      } else if (message === 'retorno_impressao_error') {
        if (this.callbacks.onError) this.callbacks.onError('Erro na impressão');
      }
    } catch (error) {
      console.error('Erro ao processar mensagem WebSocket:', error);
    }
  }

  /**
   * Envia dados para impressão via WebSocket
   */
  async sendPrintData(data) {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket não conectado'));
        return;
      }

      const originalOnMessage = this.socket.onmessage;

      this.socket.onmessage = (messageEvent) => {
        const message = messageEvent.data;
        if (message === "retorno_impressao_ok") {
          this.socket.onmessage = originalOnMessage;
          resolve();
        } else if (message === "retorno_impressao_error") {
          console.warn('Erro na impressão');
          this.socket.onmessage = originalOnMessage;
          reject(new Error('Erro na impressão'));
        }
      };

      this.socket.send(JSON.stringify(data));
    });
  }

  /**
   * Desconecta da impressora atual
   */
  async disconnect() {
    try {
      let shouldNotifyDisconnect = true;
      const wasWebBluetooth = this.connectionType === WEB_BLUETOOTH_TYPE;
      if (this.connectionType === 'bluetooth') {
        if (window.Android) {
          await window.Android.desconectar();
        }
      } else if (this.connectionType === WEB_BLUETOOTH_TYPE) {
        shouldNotifyDisconnect = false;
        await this.disconnectWebBluetooth();
      } else if (this.connectionType === 'websocket' || this.connectionType === 'socket') {
        wsPrintClient.emit(`{desparear:}`);
        try {
          wsPrintClient.close();
        } catch (error) {
          void error;
        }
      }

      this.isConnected = false;
      this.connectionType = null;
      this.printerType = null;
      this.printerName = null;
      this.deviceInfo = null;
      this.socket = null;
      if (!wasWebBluetooth) {
        this.webBluetoothDevice = null;
        this.webBluetoothServer = null;
        this.webBluetoothCharacteristic = null;
      }

      try {
        localStorage.removeItem('printerType');
        localStorage.removeItem('printerName');
      } catch (e) { void e; }

      if (shouldNotifyDisconnect && this.callbacks.onDisconnect) {
        this.callbacks.onDisconnect();
      }

      return true;
    } catch (error) {
      console.error('Erro ao desconectar:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error.message);
      }
      throw error;
    }
  }

  /**
   * Verifica se está conectado
   */
  isConnectedToPrinter() {
    const type = this.connectionType;

    if (type === 'bluetooth' || type === 'websocket' || type === 'socket' || type === WEB_BLUETOOTH_TYPE) {
      if (type === 'bluetooth') {
        if (window.Android && window.Android.getPrinterName) {
          const printerName = window.Android.getPrinterName();
          if (printerName) {
            const nome = extractPrinterName(printerName) || printerName;
            this.printerName = nome;
            this.deviceInfo = { ...(this.deviceInfo || {}), address: nome, nome };
            this.isConnected = true;
            return true;
          }
          this.isConnected = false;
          return false;
        }
      }

      if (type === WEB_BLUETOOTH_TYPE) {
        const isGattConnected = !!(this.webBluetoothDevice?.gatt?.connected && this.webBluetoothCharacteristic);
        this.isConnected = isGattConnected;
        if (isGattConnected) {
          const nome = this.webBluetoothDevice?.name || this.printerName || 'Zebra BLE';
          this.printerName = nome;
          this.deviceInfo = {
            ...(this.deviceInfo || {}),
            id: this.webBluetoothDevice?.id || null,
            nome,
            transport: 'ble',
          };
        }
        return isGattConnected;
      }

      if (type === 'websocket' || type === 'socket') {
        if (wsPrintClient && wsPrintClient.isConnected) {
          const clientUUID = wsPrintClient.uuid || localStorage.getItem('client_uuid');
          const idParear = wsPrintClient.pairedWith;
          if (clientUUID && idParear) {
            wsPrintClient.emit({ impressora: idParear, uuid: clientUUID });
          }
          return true;
        }
      }
    }

    const wsName = wsPrintClient ? wsPrintClient.printerName : null;
    const name = String(wsName || this.printerName || '').trim();
    if (name !== '') return true;
    if ((this.connectionType === 'bluetooth' || this.connectionType === WEB_BLUETOOTH_TYPE) && this.isConnected) return true;
    return false;
  }

  /**
   * Obtém informações da conexío atual
   */
  getConnectionInfo() {
    const wsName = wsPrintClient ? wsPrintClient.printerName : null;
    const wsUuid = wsPrintClient ? wsPrintClient.uuid : null;
    const wsPaired = wsPrintClient ? wsPrintClient.pairedWith : null;
    const hasWsPrinter = String(wsName || '').trim() !== '';
    const hasServicePrinter = String(this.printerName || '').trim() !== '';
    const hasBleConnection = !!(this.webBluetoothDevice?.gatt?.connected && this.webBluetoothCharacteristic);
    const effectiveConnected = hasBleConnection || hasServicePrinter || hasWsPrinter || !!this.isConnected;
    const effectiveType = effectiveConnected
      ? (this.connectionType || (hasBleConnection ? WEB_BLUETOOTH_TYPE : (hasWsPrinter ? 'websocket' : null)))
      : null;
    let effectiveDevice = this.deviceInfo ? { ...this.deviceInfo } : {};
    if (wsPaired) effectiveDevice.uuid = wsPaired;
    else if (wsUuid && !effectiveDevice.uuid) effectiveDevice.uuid = wsUuid;
    if (hasBleConnection) {
      effectiveDevice.id = this.webBluetoothDevice?.id || effectiveDevice.id || null;
      effectiveDevice.nome = this.webBluetoothDevice?.name || effectiveDevice.nome || this.printerName;
      effectiveDevice.transport = 'ble';
    }
    if (hasWsPrinter) effectiveDevice.nome = wsName; else if (hasServicePrinter) effectiveDevice.nome = this.printerName;
    if (!effectiveDevice || Object.keys(effectiveDevice).length === 0) effectiveDevice = null;

    return { isConnected: effectiveConnected, type: effectiveType, device: effectiveDevice };
  }

  /**
   * Testa a conexío atual
   */
  async testConnection() {
    try {
      if (this.connectionType === 'websocket') {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          // Envia um ping para testar
          this.socket.send(JSON.stringify({ type: 'ping' }));
          return true;
        }
        return false;
      } else if (this.connectionType === WEB_BLUETOOTH_TYPE) {
        return !!(this.webBluetoothDevice?.gatt?.connected && this.webBluetoothCharacteristic);
      } else if (this.connectionType === 'bluetooth') {
        // Para Bluetooth, assumimos que está conectado se o estado indica isso
        return this.isConnected;
      }
      return false;
    } catch (error) {
      console.error('Erro ao testar conexío:', error);
      return false;
    }
  }
}

// Instância singleton do serviço
const printerService = new PrinterService();

// Função global para ser chamada pelo Android WebView
window.setarImpressora = (deviceName) => {
  const normalizedDeviceName = extractPrinterName(deviceName);
  if (normalizedDeviceName) {
    printerService.connectionType = 'bluetooth';
    printerService.printerType = 'bluetooth';
    printerService.printerName = normalizedDeviceName;
    printerService.isConnected = true;
    printerService.deviceInfo = { ...(printerService.deviceInfo || {}), nome: normalizedDeviceName };
    console.log(`Impressora Bluetooth configurada via Android: ${normalizedDeviceName}`);

    // Opcional: salvar no localStorage para persistência
    localStorage.setItem('printerType', 'bluetooth');
    localStorage.setItem('printerName', normalizedDeviceName);

    // Notificar callbacks se houver
    if (printerService.callbacks.onConnect) {
      printerService.callbacks.onConnect({
        type: 'bluetooth',
        device: normalizedDeviceName,
        status: 'connected'
      });
    }
  }
};

const __printProgressListeners = new Set();
export const onPrintProgress = (cb) => {
  if (typeof cb === 'function') {
    __printProgressListeners.add(cb);
  }
  return () => {
    __printProgressListeners.delete(cb);
  };
};
const __emitPrintProgress = (p) => {
  try {
    __printProgressListeners.forEach((fn) => {
      try { fn(p); } catch (error) { void error; }
    });
  } catch (error) { void error; }
};

// Função utilitária para verificar conexío da impressora
export const verificaImpressora = () => {
  try {
    const psConnected = printerService.isConnectedToPrinter();
    return psConnected;
  } catch (error) {
    void error;
    return false;
  }
};

export const montarDtoEtiqueta = (notaObj, opts = {}) => {

  const currentUser = printerService.currentUser || null;
  const nbase = (notaObj && typeof notaObj === 'object') ? notaObj : {};
  const orgname = String(opts.orgname ?? currentUser?.organization?.name ?? '').trim();
  const branchsigla = String(opts.branchsigla ?? currentUser?.assignedBranch?.sigla ?? currentUser?.assignedBranch?.acronym ?? '').trim();
  const usuarioRaw = String(opts.usuario ?? currentUser?.name ?? notaObj?.veiculo?.placa ?? '').trim();
  const usuario = usuarioRaw.slice(0, 10);
  const now = opts.date instanceof Date ? opts.date : new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const aa = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const dtimpressao = String(opts.dtimpressao ?? `${dd}-${mm}-${aa}`);
  const hrimpressao = String(opts.hrimpressao ?? `${hh}:${min}`);

  const nfNumero = String(opts.nota ?? nbase?.nota ?? nbase?.numero ?? nbase?.nf ?? '').trim();
  const serie = String(opts.serie ?? nbase?.serie ?? nbase?.sr ?? '').trim();
  const remetente = String(
    opts.remetente ??
    nbase?.remetente ??
    notaObj?.remetenteNome ??
    notaObj?.remetente?.nome ??
    nbase?.emitente?.nome ??
    ''
  ).trim().substring(0, 35);
  const destinatario = String((opts.destinatario ?? nbase?.destinatario?.nome ?? nbase?.destinatario ?? nbase?.nomeDestinatario ?? '')).trim().substring(0, 35);
  const enderecoBase = String((opts.endereco ?? nbase?.destinatario?.endereco ?? nbase?.destinatario?.logradouro ?? nbase?.endereco ?? '')).trim().substring(0, 35);
  const nroBase = (opts.nro ?? nbase?.destinatario?.nro ?? nbase?.nro ?? '');
  const cidade = String((opts.cidade ?? nbase?.cidade ?? nbase?.destinatario?.cidade ?? '')).trim();
  const uf = String((opts.ufdestino ?? nbase?.destinatario?.uf ?? nbase?.uf ?? '')).trim();
  const rota = String(opts.rota ?? notaObj?.rota ?? nbase?.detalhe?.rota ?? nbase?.rota ?? '').trim();
  const cd = String(opts.cd ?? notaObj?.cd ?? nbase?.detalhe?.cd ?? nbase?.cd ?? nbase?.cddestinatario ?? '').trim();
  const setorVal = opts.setor ?? (typeof notaObj?.setor === 'number' ? notaObj?.setor : (nbase?.detalhe?.setor ?? nbase?.setor));
  const setor = String(setorVal ?? '').trim();
  const pedido = String(opts.pedido ?? nbase?.pedido ?? '').trim();
  const cepRawSource = (opts.cep ?? nbase?.cepentrega ?? nbase?.cep ?? '');
  const cepRaw = String(cepRawSource).replace(/\D/g, '');
  const cep = cepRaw.length === 8 ? `${cepRaw.slice(0, 5)}-${cepRaw.slice(5)}` : cepRaw;
  const enderecodestino = enderecoBase + ', ' + String(nroBase);
  const destinatarioCnpj = String((nbase?.destinatario?.cnpj ?? nbase?.cddestinatario ?? '')).replace(/[^a-zA-Z0-9]/g, '');
  const pessoafisica = destinatarioCnpj.length === 11 ? 'PF FISICA' : '';
  const volumeatual = String(opts.volumeAtual ?? '');
  const volumetotal = String(opts.volumeTotal ?? '');
  const sequencial = opts.sequencial != null ? String(opts.sequencial).padStart(8, '0') : '';
  const activeLabelCode = opts.activeLabelCode ?? printerService.activeLabelCode;
  const reimpressao = !!opts.reimpressao;

  return {
    orgname,
    branchsigla,
    usuario,
    dtimpressao,
    hrimpressao,
    rota,
    cd,
    setor,
    remetente,
    nota: nfNumero,
    serie,
    volumeatual,
    volumetotal,
    pedido,
    destinatario,
    enderecodestino,
    cep,
    cidadedestino: cidade,
    ufdestino: uf,
    sequencial,
    pessoafisica,
    activeLabelCode,
    reimpressao,
  };
};

export const enviarEtiquetaInicial = async (dados, nr1, nr2, activeLabelCode) => {
  const info = printerService.getConnectionInfo();
  const type = printerService.connectionType || info.type || (typeof window !== 'undefined' && window.Android ? 'bluetooth' : ((wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '')) ? 'websocket' : null));
  const btConnectedByName = (type === 'bluetooth' && String(printerService.printerName || '').trim() !== '');
  const clientUUID = wsPrintClient?.uuid || info?.device?.ip || null;
  const id_parear = info?.device?.uuid || wsPrintClient?.pairedWith || null;

  void activeLabelCode;

  const base = dados || {};

  console.log(dados)
  const clienteRaw = base?.nota?.remetente?.nome ?? base?.remetenteNome ?? base?.remetente?.nome ?? '';
  const cliente = String(clienteRaw).trim();
  const clienteFmt = cliente.slice(0, 30);
  const nfNumeroVal = base?.nota ?? base?.nota?.nota ?? base?.notaFiscal?.nota ?? base?.nota?.numero ?? base?.notaFiscal?.numero ?? base?.numero ?? null;
  const nfNumero = nfNumeroVal != null ? (String(nfNumeroVal).match(/\d{1,}/)?.[0] ?? '') : '';

  const v1 = String(nr1 ?? '').trim();
  const v2 = String(nr2 ?? '').trim();

  const chave = (() => {
    const c1 = base?.chaveNfe;
    const c2 = base?.chave;
    const c3 = base?.nota?.chaveNfe;
    const c4 = base?.nota?.chave;
    let c = c1 ?? c2 ?? c3 ?? c4 ?? '';
    if (!c) {
      try {
        const m = String(base?.dados || '').match(/\d{44}/);
        c = m ? m[0] : '';
      } catch (error) {
        void error;
      }
    }
    return String(c).trim();
  })();

  let zpl = '';
  zpl += '^XA';
  zpl += '^PW800';
  zpl += '^LL400';
  zpl += '^LH0,0';
  zpl += '^CI28';
  zpl += `^FO0,30^FB450,1,0,C,0^A0N,30,25^FD${clienteFmt}^FS`;
  if (nfNumero) {
    zpl += `^FO0,65^FB450,1,0,C,0^A0N,30,30^FDNota: ${nfNumero}^FS`;
  }

  if (chave) {
    zpl += `^FO480,40^BQN,2,6^FDQA,${chave}^FS`;
  }

  zpl += '^BY3,2,120';
  zpl += `^FO60,120^BCN,80,Y,N,N^FD${v1}^FS`;
  zpl += `^FO60,250^BCN,80,Y,N,N^FD${v2}^FS`;
  zpl += '^XZ';

  const useWsClient = !!(wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '' || String(wsPrintClient.uuid || '').trim() !== ''));
  const bleConnected = type === WEB_BLUETOOTH_TYPE && !!(printerService.webBluetoothDevice?.gatt?.connected && printerService.webBluetoothCharacteristic);

  if (type === WEB_BLUETOOTH_TYPE) {
    if (!bleConnected) throw new Error('Web Bluetooth desconectado');
    await printerService.writeWebBluetoothChunks(zpl);
    return true;
  }

  if (type === 'bluetooth') {
    if (window.Android) {
      window.Android.imprimir(zpl);
      return true;
    }
    if (!(info.isConnected || btConnectedByName)) throw new Error('Bluetooth indisponível e WebSocket não conectado');
  }

  if (useWsClient && (type === 'socket' || type === 'websocket')) {
    wsPrintClient.emit({ imprimir: id_parear, zpl, uuid: clientUUID });
    return true;
  }

  throw new Error('Tipo de impressora não definido');
};

// Função exportável para imprimir usando a conexío atual
export const imprimir = async (data, activeLabelCode) => {

  try { await printerService.ensureActiveLabelCode(); } catch (error) { void error; }

  const btByName = String(printerService.printerName || '').trim() !== '';
  const isConnected = !!printerService.isConnected || !!(wsPrintClient && wsPrintClient.isConnected) || btByName;
  const printerName = (
    extractPrinterName(printerService.printerName) ||
    extractPrinterName(wsPrintClient ? wsPrintClient.printerName : null) ||
    extractPrinterName(data?.printername) ||
    extractPrinterName(data?.impressora) ||
    extractPrinterName(data?.printer) ||
    extractPrinterName(data?.deviceName)
  );

  if (!isConnected || !printerName) {
    try { message.error('Impressora não conectada'); } catch (error) { void error; }
    throw new Error('Impressora não conectada');
  }


  const currentUser = printerService.currentUser || null;
  const chave = (() => {
    const c1 = data?.chaveNfe;
    const c2 = data?.chave;
    const c3 = data?.nota?.chaveNfe;
    const c4 = data?.nota?.chave;
    let c = c1 ?? c2 ?? c3 ?? c4 ?? '';
    if (!c) {
      try {
        const m = String(data?.dados || '').match(/\d{44}/);
        c = m ? m[0] : '';
      } catch (error) {
        void error;
      }
    }
    return String(c).trim();
  })();
  if (!chave) throw new Error('Chave da NF-e não informada');

  data.chave = chave;
  if (data.nota && typeof data.nota === 'object') {
    data.nota.chave = chave;
  }

  const finalPrinter = extractPrinterName(printerName);

  const filialRaw = currentUser?.assignedBranch?.codigo;
  const filialInt = filialRaw == null ? NaN : parseInt(String(filialRaw), 10);

  const payload = {
    chave,
    userId: (data?.userId || data?.usuarioId || currentUser?.id) || null,
    organizationId: (data?.organizationId || currentUser?.organizationId) || null,
    assignedBranchId: (data?.assignedBranchId || currentUser?.assignedBranchId) || null,
    printername: (finalPrinter || '').slice(0, 100),
    filial: Number.isInteger(filialInt) ? filialInt : undefined,
    placa: data?.nota?.veiculo?.placa || data?.placa || null,
    veiculoId: data?.veiculoId || data?.nota?.veiculo?.id || null,
    // obrigatórios no ImprimirDto
    cnpjrmte: (() => {
      let c = (data?.nota?.cnpjrmte ??
        data?.cdremetente ??
        data?.cnpjrmte ??
        data?.nota?.CNPJ ?? '');

      // Fallback: extrair da chave (posições 7-20, ou seja, index 6-20)
      if ((!c || String(c).trim() === '') && chave.length === 44) {
        c = chave.substring(6, 20);
      }

      return String(c || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
    })(),
    nota: (() => {
      let n = (data?.nota?.nota ?? data?.notaNumero ?? data?.nota_numero ?? data?.notaNro);

      // Se n for nulo e data.nota for número, usamos data.nota
      if (n == null && typeof data?.nota === 'number') {
        n = data.nota;
      }

      // Fallback: extrair da chave (posições 26-34, ou seja, index 25-34)
      if (n == null && chave.length === 44) {
        n = parseInt(chave.substring(25, 34), 10);
      }

      return n == null ? undefined : parseInt(String(n), 10);
    })(),
    serie: (() => {
      let s = (data?.nota?.serie ?? data?.serie ?? '');

      // Fallback: extrair da chave (posições 23-25, ou seja, index 22-25)
      if ((!s || String(s).trim() === '') && chave.length === 44) {
        s = chave.substring(22, 25);
      }

      return String(s || '').slice(0, 3);
    })(),
    volumes: (() => {
      const v = (data?.nota?.detalhe?.volumes ?? data?.nota?.volumes ?? data?.volumes);
      return v == null ? undefined : parseInt(String(v), 10);
    })(),
    // opcionais
    rota: data?.rota || data?.nota?.detalhe?.rota || data?.nota?.rota || undefined,
    cd: data?.cd || data?.nota?.detalhe?.cd || data?.nota?.cd || undefined,
    setor: (typeof data?.setor === 'number' ? data?.setor : (data?.nota?.detalhe?.setor ?? data?.nota?.setor)),
    cepentrega: data?.cepentrega || data?.nota?.cepentrega || undefined,
  };

  //aviso de quantidade alta de volumes
  if (payload.volumes > 100) {
    try { message.warning('Quantidade de volumes maior que 100. A impressão pode demorar.\nEm dispositivos móveis não bloqueia a interface.'); } catch (error) { void error; }
  }

  //return;
  const res = await notaanota.imprimir(payload);
  // Validação do retorno da requisição
  if (!res || res.ok === false || res.status !== 200) {
    if (res?.message) {
      try { message.error(res.message); } catch (error) { void error; }
    }
    return false;
  }

  // Extração e validação obrigatória de nr1 e nr2 (não podem ser 0, vazios ou estáticos)
  const baseNr1 = parseInt(res?.nr1Num ?? res?.nr1 ?? 0);
  const baseNr2 = parseInt(res?.nr2Num ?? res?.nr2 ?? 0);

  if (!baseNr1 || !baseNr2 || isNaN(baseNr1) || isNaN(baseNr2)) {
    console.error('nr1 ou nr2 inválidos no retorno:', { nr1: baseNr1, nr2: baseNr2 });
    return false;
  }

  try {
    let volTotal = 0;
    let lastVol = 0;

    const volFromNota = Number((data?.nota?.detalhe?.volumes ?? data?.nota?.volumes ?? 0));
    // Prioriza o totalVolumes retornado pelo backend
    const totalVolumesCalculado = res?.totalVolumes ?? (baseNr2 - baseNr1);
    volTotal = Number(totalVolumesCalculado || volFromNota || 0);
    __emitPrintProgress({ total: volTotal, current: 0 });
    let printedOk = true;


    for (let voli = 1; voli <= volTotal; voli++) {
      // A variável sequencial é obrigatória baseada no cálculo: nr1 + voli
      // Onde voli começa em 1, garantindo que o primeiro seja nr1 + 1
      const seq = baseNr1 + voli;
      const seqStr = String(seq).padStart(8, '0');
      const dto = montarDtoEtiqueta(data?.nota || null, {
        volumeAtual: String(voli),
        volumeTotal: String(volTotal),
        sequencial: seqStr,
        reimpressao: false,
        activeLabelCode: activeLabelCode ?? printerService.activeLabelCode,
        rota: data?.rota,
        cd: data?.cd,
        setor: (typeof data?.setor === 'number' ? data?.setor : undefined),
        pedido: data?.nota?.pedido,
        cep: data?.cepentrega,
        usuario: (printerService.currentUser?.name || data?.placa || ''),
      });


      await enviarparaimpressora(dto).catch(() => { printedOk = false; });
      if (!printedOk) break;
      lastVol = voli;
      __emitPrintProgress({ total: volTotal, current: voli });
      if (voli % 10 === 0 && voli < volTotal) {
        const delayMs = volTotal > 499 ? 5000 : 2000;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    __emitPrintProgress({ total: volTotal, current: lastVol, done: true, ok: printedOk });

    try {
      await enviarEtiquetaInicial(data?.nota || null, res.nr1, res.nr2, printerService.activeLabelCode);
    } catch (error) {
      void error;
      try { message.warning('Falha ao imprimir etiqueta inicial'); } catch (messageError) { void messageError; }
    }

    return res;

  } catch (error) {
    void error;
    try { __emitPrintProgress({ total: 0, current: 0, done: true, ok: false }); } catch (progressError) { void progressError; }
    return false;
  }

};

// Função para enviar diretamente para impressora com base na nota atual e intervalo de volumes
export const enviarparaimpressora = async (dto) => {

  printerService.isConnectedToPrinter();

  if (!printerService.connectionType) {
    const savedType = localStorage.getItem('printerType');
    if (savedType) {
      printerService.connectionType = savedType;
      printerService.printerType = savedType;
    }
  }
  if (!printerService.printerName) {
    const savedName = localStorage.getItem('printerName');
    if (savedName) {
      printerService.printerName = savedName;
    }
  }

  const info = printerService.getConnectionInfo();
  const type = printerService.connectionType || info.type || (typeof window !== 'undefined' && window.Android ? 'bluetooth' : ((wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '')) ? 'websocket' : null));
  const btConnectedByName = (type === 'bluetooth' && String(printerService.printerName || '').trim() !== '');
  const clientUUID = wsPrintClient?.uuid || info?.device?.ip || null;
  const id_parear = info?.device?.uuid || wsPrintClient?.pairedWith || null;



  const currentUser = printerService.currentUser || null;
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const aa = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const dtDefault = `${dd}-${mm}-${aa}`;
  const hrDefault = `${hh}:${min}`;

  let zpl = `
      ^XA~SD15^PRC^MMT^LH0030,0040^FS
      ^FO210,0^AAN,18,10^FD$orgname^FS
      ^FO210,20^AAN,18,10^FD$branchsigla $usuario $dtimpressao $hrimpressao ETQAPP^FS
      ^FO210,42^GB419,118,59,B^FS
      ^FO238,55^A0N,120,100^FR^FD$rota^FS
      ^FO475,55^FB144,1,0,R,0^A0N,45,45^FR^FD$cd^FS
      ^FO460,101^A0N,60,70^FR^FB159,1,0,R,0^FD$setor^FS
      ^FO210,168^AAN,18,10^FD$remetente^FS
      ^FO210,194^AAN,18,10^FDNF^FS
      ^FO235,194^A0N,45,50^FD$nota^FS
      ^FO410,213^AAN,18,10^FD$serie^FS
      ^FO430,194^A0N,45,50^FB199,1,0,R,0^FD$volumeatual/$volumetotal^FS
      ^FO210,238^AAN,18,10^FD$pedido^FS
      ^FO210,260^GB419,2,2^FS
      ^FO210,268^AAN,18,10^FD$destinatario^FS
      ^FO210,312^A0N,25,20^FB419,3,0,L,0
      ^FD$enderecodestino^FS
      ^FO210,290^AAN,18,10^FD$cep $cidadedestino/$ufdestino^FS
      ^FO45,0^BY4.0,2.5^BCR,140,N,N,N,A^FD$sequencial^FS
      ^FO10,70^A0R,30,30^FD$sequencial^FS
      ^XZ
    `;

  try {
    const orgDominio = String(dto?.orgdominio ?? dto?.orgname ?? currentUser?.organization?.dominio ?? '').trim();
    const branchSigla = String(dto?.branchsigla ?? currentUser?.assignedBranch?.sigla ?? currentUser?.assignedBranch?.acronym ?? '').trim();
    const usuario = String(dto?.usuario ?? '').trim();
    const dtimpressao = String(dto?.dtimpressao ?? dtDefault);
    const hrimpressao = String(dto?.hrimpressao ?? hrDefault);
    const rotaF = String(dto?.rota ?? '');
    const cdF = String(dto?.cd ?? '');
    const setorF = String(dto?.setor ?? '');
    const remetente = String(dto?.remetente ?? '');
    const nfNumeroF = String(dto?.nota ?? '');
    const serieF = String(dto?.serie ?? '');
    const volumeatual = String(dto?.volumeatual ?? '');
    const volumetotal = String(dto?.volumetotal ?? '');
    const pedido = String(dto?.pedido ?? '');
    const destinatarioF = String(dto?.destinatario ?? '');
    const cep = String(dto?.cep ?? '');
    const cidade = String(dto?.cidadedestino ?? '');
    const ufdestino = String(dto?.ufdestino ?? '');
    const enderecoFinal = String(dto?.enderecodestino ?? '');
    const nrCode = String(dto?.sequencial ?? '');

    if (!nrCode) throw new Error('Sequencial é obrigatório');

    const pessoafisica = dto?.pessoafisica === 'PF FISICA' ? 'PF FISICA' : '';

    zpl = zpl.replace('$orgname', orgDominio);
    zpl = zpl.replace('$branchsigla', branchSigla);
    zpl = zpl.replace('$usuario', usuario);
    zpl = zpl.replace('$dtimpressao', dtimpressao);
    zpl = zpl.replace('$hrimpressao', hrimpressao);
    zpl = zpl.replace('$rota', rotaF);
    zpl = zpl.replace('$cd', cdF);
    zpl = zpl.replace('$setor', setorF);
    zpl = zpl.replace('$remetente', remetente);
    zpl = zpl.replace('$nota', nfNumeroF);
    zpl = zpl.replace('$serie', serieF);
    zpl = zpl.replace('$volumeatual', volumeatual);
    zpl = zpl.replace('$volumetotal', volumetotal);
    zpl = zpl.replace('$pedido', pedido);
    zpl = zpl.replace('$destinatario', destinatarioF);
    zpl = zpl.replace('$cep', cep);
    zpl = zpl.replace('$cidadedestino', cidade);
    zpl = zpl.replace('$ufdestino', ufdestino);
    zpl = zpl.replace('$enderecodestino', enderecoFinal);
    zpl = zpl.replace('$sequencial', nrCode);
    zpl = zpl.replace('$sequencial', nrCode);
    if (pessoafisica === 'PF FISICA') {
      //zpl = zpl.replace('$pessoafisica', pessoafisica);
    } else {
      //zpl = zpl.replace('^FO250,0^GB36,336,30,B^FS','');
      //zpl = zpl.replace('^FO250,113^A0R,30,30^FR^FD$pessoafisica^FS', '');
    }
  } catch (error) {
    if (error.message === 'Sequencial é obrigatório') throw error;
  }

  const bleConnected = type === WEB_BLUETOOTH_TYPE && !!(printerService.webBluetoothDevice?.gatt?.connected && printerService.webBluetoothCharacteristic);
  if (type === WEB_BLUETOOTH_TYPE || printerService.printerType === WEB_BLUETOOTH_TYPE) {
    if (!bleConnected) {
      throw new Error('Web Bluetooth desconectado');
    }
    await printerService.writeWebBluetoothChunks(zpl);
    return true;
  }

  if (type === 'bluetooth') {
    if (window.Android) {
      window.Android.imprimir(zpl);
      return true;
    }
    if (!(info.isConnected || btConnectedByName)) throw new Error('Bluetooth indisponível e WebSocket não conectado');
  }

  const useWsClient = !!(wsPrintClient && (wsPrintClient.isConnected || String(wsPrintClient.printerName || '').trim() !== '' || String(wsPrintClient.uuid || '').trim() !== ''));
  if (useWsClient && (type === 'socket' || type === 'websocket')) {
    wsPrintClient.emit({ imprimir: id_parear, zpl, uuid: clientUUID });
    return true;
  }

  throw new Error('Tipo de impressora não definido');
};

export default printerService;
